import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {  LEAVES_BY_AGENCY } from "../../api/query";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../components/common/Loader";
import DynamicTable from "../../components/common/DynamicTable";
import { differenceInMinutes, endOfMonth, format, parse, parseISO, startOfMonth } from "date-fns";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useAuth } from "../../context/AuthContext";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UPDATE_LEAVE_STATUS } from "../../api/mutation";
import { showError, showSuccess } from "../../components/common/ToastNotification";

const leaveManagementSchema = yup.object().shape({
    monthName: yup.string().required("Month is required"),
    yearName: yup.string().required("Year is required"),
    assignedMemberID: yup.string().required("Employee is required"),
    assignedMember: yup.string().required("Employee is required"),
});

const LeaveManagement = () => {
    const navigate = useNavigate();
    const { userInfo } = useAuth()

    const agencyID = userInfo?.agencies?.id

    //constants
    const [sorting, setSorting] = useState<SortingState>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [mainTable, setMainTable] = useState<any>(null);
    const [mainTableUpdate, setMainTableUpdate] = useState(false);

    const [monthOptions, setMonthOptions] = useState<{ key: string; name: string }[]>([]);
    const [yearOptions, setYearOptions] = useState<{ key: string; name: string }[]>([]);

    // const [assignedMemberOptions, setAssignedMemberOptions] = useState<{ key: string; name: string }[]>([]);

    //api
    const [leavesByAgency, { data: leavesData, loading: leavesDataLoading }] = useLazyQuery(LEAVES_BY_AGENCY, {
        fetchPolicy: 'no-cache'
    })

    const [updateLeaveStatus, { loading: updateLeaveStatusLoading }] = useMutation(UPDATE_LEAVE_STATUS, {
        fetchPolicy: 'no-cache',
    })

    // const [
    //     getEmployeesByAgencyID,
    //     { data: employeesData, loading: employeesDataLoading },
    // ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
    //     fetchPolicy: 'no-cache'
    // });

    const {
        setValue,
        formState: { errors },
        watch,
    } = useForm<any>({
        resolver: yupResolver(leaveManagementSchema),
    });

    //watch
    const watchMonth = watch("monthName");
    const watchYear = watch("yearName");
    // const watchAssignedMember = watch("assignedMember");
    // const watchAssignedMemberID = watch('assignedMemberID');

    useEffect(() => {
        const currentDate = new Date();

        const monthNames = Array.from({ length: 12 }, (_, i) => ({
            key: (i + 1).toString().padStart(2, "0"),
            name: format(new Date(2000, i), "MMMM"),
        }));

        const currentYear = currentDate.getFullYear();
        const yearRange = Array.from({ length: 5 }, (_, i) => currentYear - i);
        const yearOptionsData = yearRange.map((year) => ({
            key: year.toString(),
            name: year.toString(),
        }));

        setMonthOptions(monthNames);
        setYearOptions(yearOptionsData);

        // Get current month object
        const defaultMonth = monthNames[currentDate.getMonth()];
        const defaultYear = currentYear.toString();

        setValue("monthName", defaultMonth.key);  // form stores key
        setValue("yearName", defaultYear);
    }, []);

    // useEffect(() => {
    //     if (organizationID) {
    //         getEmployeesByAgencyID({
    //             variables: {
    //                 agency_id: parseInt(organizationID)
    //             }
    //         });
    //     }
    // }, [organizationID])


    useEffect(() => {
        const selectedMonth = parseInt(watchMonth);
        const selectedYear = parseInt(watchYear);

        if (selectedMonth && selectedYear) {
            const firstDayOfMonth = startOfMonth(new Date(selectedYear, selectedMonth - 1));
            const lastDayOfMonth = endOfMonth(new Date(selectedYear, selectedMonth - 1));
            // const today = new Date();

            // const isCurrentMonth =
            //     selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1;

            const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
            const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');
            // const formattedEndDate = format(
            //     isCurrentMonth ? today : lastDayOfMonth,
            //     'yyyy-MM-dd'
            // );

            leavesByAgency({
                variables: {
                    agency_id: agencyID,
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                    leave_status: ["Pending"]
                }
            })
        }
    }, [watchMonth, watchYear]);

    // useEffect(() => {
    //     if (employeesData && employeesData.employees_by_agency_id) {
    //         const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
    //             key: emp.employee_id,
    //             name: `${emp.first_name} ${emp.last_name}`
    //         }));
    //         setAssignedMemberOptions(employeeOptions);

    //         const defaultMember = employeeOptions?.find((member: any) => member.key == employeeID);

    //         if (defaultMember) {
    //             // Set default assigned member
    //             setValue('assignedMemberID', defaultMember.key);
    //             setValue('assignedMember', defaultMember.name);
    //             // trigger('assignedMember');
    //         } else {
    //             // Clear if employeeID not found in members
    //             setValue('assignedMemberID', '');
    //             setValue('assignedMember', '');
    //         }
    //     }
    // }, [employeesData])

    // Use useMemo to optimize the column generation
    const columns: any[] = [
        {
            key: "employee_name",
            name: "Employee Name",
            id: "employee_name",
            header: "Employee Name",
            accessorKey: "employee_name",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "leave_type",
            name: "Leave Type",
            id: "leave_type",
            header: "Leave Type",
            accessorKey: "leave_type",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "leave_reason",
            name: "Reason",
            id: "leave_reason",
            header: "Reason",
            accessorKey: "leave_reason",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "leave_date",
            name: "Date",
            id: "leave_date",
            header: "Date",
            accessorKey: "leave_date",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any) => {
                const startDateA = rowA?.getValue("leave_date");
                const startDateB = rowB?.getValue("leave_date");

                // If a start_date is missing (null or undefined), treat it as the latest date.
                // You can also treat missing dates as the earliest date by using `new Date(0)` instead of `new Date()`
                const defaultDate = new Date(0); // You can change this to `new Date()` if you want it sorted to the end.

                const dateA = startDateA ? parse(startDateA, "MM-dd-yyyy", new Date()) : defaultDate;
                const dateB = startDateB ? parse(startDateB, "MM-dd-yyyy", new Date()) : defaultDate;

                // Ensure valid date parsing, otherwise fallback to defaultDate
                if (isNaN(dateA.getTime())) {
                    dateA.setTime(defaultDate.getTime());
                }
                if (isNaN(dateB.getTime())) {
                    dateB.setTime(defaultDate.getTime());
                }

                // Compare the full dates directly (ascending or descending will be handled by react-table)
                return dateA.getTime() - dateB.getTime();
            },
        },
        {
            key: "status",
            name: "Status",
            id: "status",
            header: "Status",
            accessorKey: "status",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "action",
            name: "Action",
            id: "action",
            header: "Action",
            accessorKey: "action",
            hideSearch: true,
            cell: ({ row }: any) => {
                const original = row.original;
                return (
                    <div className="flex items-center gap-[8px]  theme_btn_item">
                        <button
                            className="dash-action-link cursor-pointer"
                            onClick={() => {
                                handleApproveLeave(row.original.id)
                            }}
                        >
                            <span className="text-sm dark:text-white">YES</span>
                        </button>

                        <button
                            className="dash-action-link cursor-pointer"
                            onClick={() => {
                                handleRejectLeave(row.original.id)
                            }}
                        >
                            <span className="text-sm dark:text-white">NO</span>
                        </button>
                    </div>
                );
            },
        },
    ];

    const formattedData = useMemo(() => {
        if (leavesData && leavesData?.leaves_by_agency?.status_code === 200) {
            // setTotalPages(leavesData?.defects?.pagination.total_pages)
            const upcomingLeaves = leavesData?.leaves_by_agency?.leaves || [];
            return upcomingLeaves.map((ul: any) => {
                return {
                    id: ul.id,
                    employee_name: ul.employee_name,
                    leave_type: ul.leave_type,
                    leave_reason: ul.reason,
                    status: ul.status,
                    leave_date: format(new Date(ul.start_date), 'MM-dd-yyyy'),
                };
            });
        } else {
            return [];
        }
    }, [leavesData]);

    function handleSelectMonth(key: number, name: string): void {
        setValue('monthName', key)
    }

    function handleSelectYear(key: number, name: string): void {
        setValue('yearName', key)
    }

    async function handleApproveLeave(id: string) {
        try {
            const response = await updateLeaveStatus({
                variables: {
                    ids: [id],
                    leave_status: "Approved"
                }
            });
            if (response.data.update_leave_status.status_code === 200) {
                showSuccess(response.data.update_leave_status.message)

                const firstDayOfMonth = startOfMonth(new Date(watchYear, watchMonth - 1));
                const lastDayOfMonth = endOfMonth(new Date(watchYear, watchMonth - 1));
                // const today = new Date();

                // const isCurrentMonth =
                //     selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1;

                const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
                const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');
                // const formattedEndDate = format(
                //     isCurrentMonth ? today : lastDayOfMonth,
                //     'yyyy-MM-dd'
                // );

                leavesByAgency({
                    variables: {
                        agency_id: agencyID,
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        leave_status: ["Pending"]
                    }
                })
            }
            if (response.data.update_leave_status.status_code === 400) {
                showError(response.data.update_leave_status.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    async function handleRejectLeave(id: string) {
        try {
            const response = await updateLeaveStatus({
                variables: {
                    ids: [id],
                    leave_status: "Rejected"
                }
            });
            if (response.data.update_leave_status.status_code === 200) {
                showSuccess(response.data.update_leave_status.message)

                const firstDayOfMonth = startOfMonth(new Date(watchYear, watchMonth - 1));
                const lastDayOfMonth = endOfMonth(new Date(watchYear, watchMonth - 1));
                // const today = new Date();

                // const isCurrentMonth =
                //     selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1;

                const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
                const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');
                // const formattedEndDate = format(
                //     isCurrentMonth ? today : lastDayOfMonth,
                //     'yyyy-MM-dd'
                // );

                leavesByAgency({
                    variables: {
                        agency_id: agencyID,
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        leave_status: ["Pending"]
                    }
                })
            }
            if (response.data.update_leave_status.status_code === 400) {
                showError(response.data.update_leave_status.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    async function handleApproveAllClick() {
        const selectedLeaveIds = mainTable
            .getRowModel()
            .rows
            .filter((row: any) => selectedRows.has(row.id))
            .map((row: any) => {
                return row.original.id; // Get the ids from the original row data
            });

        try {
            const response = await updateLeaveStatus({
                variables: {
                    ids: selectedLeaveIds,
                    leave_status: "Approved"
                }
            });
            if (response.data.update_leave_status.status_code === 200) {
                showSuccess(response.data.update_leave_status.message)

                const firstDayOfMonth = startOfMonth(new Date(watchYear, watchMonth - 1));
                const lastDayOfMonth = endOfMonth(new Date(watchYear, watchMonth - 1));
                // const today = new Date();

                // const isCurrentMonth =
                //     selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1;

                const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
                const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');
                // const formattedEndDate = format(
                //     isCurrentMonth ? today : lastDayOfMonth,
                //     'yyyy-MM-dd'
                // );

                leavesByAgency({
                    variables: {
                        agency_id: agencyID,
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        leave_status: ["Pending"]
                    }
                })
            }
            if (response.data.update_leave_status.status_code === 400) {
                showError(response.data.update_leave_status.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }

    }

    async function handleRejectAllClick() {
        const selectedLeaveIds = mainTable
            .getRowModel()
            .rows
            .filter((row: any) => selectedRows.has(row.id))
            .map((row: any) => {
                return row.original.id; // Get the ids from the original row data
            });

        try {
            const response = await updateLeaveStatus({
                variables: {
                    ids: selectedLeaveIds,
                    leave_status: "Rejected"
                }
            });
            if (response.data.update_leave_status.status_code === 200) {
                showSuccess(response.data.update_leave_status.message)

                const firstDayOfMonth = startOfMonth(new Date(watchYear, watchMonth - 1));
                const lastDayOfMonth = endOfMonth(new Date(watchYear, watchMonth - 1));
                // const today = new Date();

                // const isCurrentMonth =
                //     selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1;

                const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
                const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');
                // const formattedEndDate = format(
                //     isCurrentMonth ? today : lastDayOfMonth,
                //     'yyyy-MM-dd'
                // );

                leavesByAgency({
                    variables: {
                        agency_id: agencyID,
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        leave_status: ["Pending"]
                    }
                })

            }
            if (response.data.update_leave_status.status_code === 400) {
                showError(response.data.update_leave_status.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }


    return (
        <>
            <div id="cts-respon-p-dv" className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full">
                <div className="min-w-full flex flex-col items-start justify-start gap-6">
                    <BreadCumb />

                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Leave Management</h3>
                    </div>

                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">

                        {/* {employeeID == "1000064" && (
                            <SearchableInputsDropdown
                                label="Employee*"
                                initialValue={watchAssignedMember}
                                placeHolder="Select Member"
                                options={assignedMemberOptions}
                                onSelect={(key: number, name: string) => {
                                    // setClearMemberSelected(false);
                                    setValue('assignedMemberID', key);
                                    setValue('assignedMember', name);
                                    // trigger('assignedMember');
                                }}
                                errorMessage={errors?.assignedMember}
                            // clearSelected={clearMemberSelected}
                            />
                        )} */}

                        <SearchableInputsDropdown
                            label="Month*"
                            initialValue={
                                monthOptions.find(m => m.key === watchMonth)?.name || ""
                            }
                            placeHolder="Select Month"
                            options={monthOptions}
                            onSelect={handleSelectMonth}
                            errorMessage={errors?.monthName}
                        />

                        <SearchableInputsDropdown
                            label="Year*"
                            initialValue={
                                yearOptions.find(y => y.key === watchYear)?.name || ""
                            }
                            placeHolder="Select Year"
                            options={yearOptions}
                            onSelect={handleSelectYear}
                            errorMessage={errors?.yearName}
                        />
                    </div>

                    {(formattedData?.length > 0) ? (
                        <div className="w-full">
                            <DynamicTable
                                filteredData={formattedData}
                                selectedColumns={columns}
                                sorting={sorting}
                                setSorting={setSorting}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                setMainTable={setMainTable}
                                setMainTableUpdate={setMainTableUpdate}
                                showAllCheckbox={true}
                            />
                        </div>
                    ) : (
                        <div className="self-stretch rounded-lg  overflow-hidden rounded-lg flex flex-col items-center justify-center  gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                            <div className="self-stretch bg-white rounded-lg bg-slate-100 overflow-hidden dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                                {/* <img
                            className="w-9 h-9 relative overflow-hidden shrink-0"
                            alt=""
                            // src={alertOrAction === "action" ? firsflag1 : iioniconaalertcircleoutline}
                        /> */}
                                <p className="relative my-0">No data available</p>
                            </div>
                        </div>
                    )}

                  <div className="w-full gap-[16px] flex justify-end">
                      <button className={`cursor-pointer [border:none] py-[12.5px] px-[26px] bg-green-500  hover:bg-green-600 rounded-radiuscomponent-button flex flex-row items-center justify-center gap-spacing-sm whitespace-nowrap ${selectedRows?.size > 0 ? "" : 'disable_button-blg'}`}
                        disabled={selectedRows?.size > 0 ? false : true}
                        onClick={handleApproveAllClick}
                        type="button"
                    >
                        <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block min-w-[83px]">
                            Approve
                        </div>
                    </button>

                    <button className={`cursor-pointer [border:none] py-[12.5px] px-[26px] red-clockout-btn rounded-radiuscomponent-button flex flex-row items-center justify-center gap-spacing-sm whitespace-nowrap  ${selectedRows?.size > 0 ? "" : 'disable_button-blg'}`}
                        disabled={selectedRows?.size > 0 ? false : true}
                        onClick={handleRejectAllClick}
                        type="button"
                    >
                        <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block min-w-[83px]">
                            Reject
                        </div>
                    </button>
                  </div>

                    {(leavesDataLoading || updateLeaveStatusLoading) && (
                        <Loader />
                    )}
                </div>
            </div>
        </>
    )
}

export default LeaveManagement; 